import { useCallback, useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@tanstack/react-query';
import { getPublisherIdByPortalId } from 'utils/portalUtils';

import {
  FormValues,
  IExecuteResultDto,
  IJSONSchema,
  RequestHeaders
} from 'components/IntegrationTestModal/integration-test-modal.types';

import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';
import { useUserDetails } from './useUserDetails';

export enum IntegrationDomain {
  AWARD = 'award',
  INITIATE_GAME_AUTH = 'initiate-game-auth',
  AUTHENTICATE_PLAYER = 'authenticate-player',
  PERSONALIZATION = 'personalization',
  EVENTS_CENTER = 'events-center'
}

export enum IntegrationVersion {
  V1 = 'v1',
  V2 = 'v2'
}

interface IUsePublisherIntegrationTestParams {
  publisherId: string;
  domain: IntegrationDomain;
  version: IntegrationVersion;
  schemaName?: string;
  enabled?: boolean;
}

export default function usePublisherIntegrationTest({
  publisherId,
  domain,
  version,
  schemaName,
  enabled = true
}: IUsePublisherIntegrationTestParams) {
  const axios = useAxios();
  const { portalId } = useParams<{ portalId: string }>();
  const { userDetails } = useUserDetails();
  
  const effectivePublisherId = publisherId || (portalId ? getPublisherIdByPortalId(portalId, userDetails) : undefined);
  const publisherHeader = useMemo(
    () => effectivePublisherId ? { 'x-publishers': effectivePublisherId } : undefined,
    [effectivePublisherId]
  );

  const basePath = getApiRouteOrMock(EApiRoutes.PUBLISHER_INTEGRATION_TEST);

  const getSchemaNames = useQuery<string[], Error>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.INTEGRATION, 'schema-names', domain, version, effectivePublisherId],
    queryFn: async () => {
      return await axios.get(
        `${basePath}/${domain}/request/${version}/schema-names`,
        {},
        publisherHeader
      );
    },
    enabled: enabled && !!effectivePublisherId
  });

  const getRequestBodySchema = useQuery<IJSONSchema, Error>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.INTEGRATION, 'schema', domain, version, schemaName, effectivePublisherId],
    queryFn: async () => {
      return await axios.get(
        `${basePath}/${domain}/request/${version}/schemas/${schemaName}`,
        {},
        publisherHeader
      );
    },
    enabled: enabled && !!effectivePublisherId && !!schemaName
  });

  const fetchRequestHeaders = useCallback(
    async (payload: FormValues): Promise<RequestHeaders> => {
      const response = await axios.post(
        `${basePath}/${domain}/request/${version}/headers`,
        payload,
        publisherHeader
      );
      return response.data;
    },
    [axios, basePath, domain, version, publisherHeader]
  );

  const getExamplePayload = useQuery<FormValues, Error>({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.INTEGRATION, 'example', domain, version, schemaName, effectivePublisherId],
    queryFn: async () => {
      return await axios.get(
        `${basePath}/${domain}/request/${version}/schemas/${schemaName}/example`,
        {},
        publisherHeader
      );
    },
    enabled: enabled && !!effectivePublisherId && !!schemaName
  });

  const executeTest = useMutation<IExecuteResultDto, Error, { headers: RequestHeaders; payload: FormValues }>(
    async ({ headers, payload }) => {
      const response = await axios.post(
        `${basePath}/${domain}/${version}/execute`,
        { headers, payload },
        publisherHeader
      );
      return response.data;
    }
  );

  const isLoading =
    getSchemaNames.isLoading ||
    getRequestBodySchema.isLoading ||
    getExamplePayload.isLoading;

  const isDataReady =
    !!getRequestBodySchema.data &&
    !!getExamplePayload.data;

  return {
    getSchemaNames,
    getRequestBodySchema,
    fetchRequestHeaders,
    getExamplePayload,
    executeTest,
    isLoading,
    isDataReady
  };
}